import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { 
  Users, Clock, Building2, Plus, Trash2, Calculator, 
  CheckCircle, AlertCircle, Save, DollarSign, Loader2,
  ChevronRight, Edit2, UserPlus
} from "lucide-react";
import { formatCurrency, formatPercent } from "@/lib/config";
import { toast } from "sonner";
import api from "@/lib/api";

const DEPARTMENTS = ["PRQC", "LMDS", "Location", "GLOBAL"];

export const VentilationPaie = () => {
  const [year, setYear] = useState(2025);
  const [loading, setLoading] = useState(true);
  const [payrollTransactions, setPayrollTransactions] = useState([]);
  const [adminEmployees, setAdminEmployees] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Dialog states
  const [isEmployeeDialogOpen, setIsEmployeeDialogOpen] = useState(false);
  const [isClassifyDialogOpen, setIsClassifyDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [classifyingTransaction, setClassifyingTransaction] = useState(null);
  
  // Form states
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    ventilation: [{ departement: "PRQC", pourcentage: 100 }]
  });
  const [classificationForm, setClassificationForm] = useState({
    adminDetails: [],
    terrainVentilation: [
      { departement: "PRQC", pourcentage: 60 },
      { departement: "LMDS", pourcentage: 30 },
      { departement: "Location", pourcentage: 10 }
    ]
  });

  // Load data
  useEffect(() => {
    loadData();
  }, [year]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load payroll transactions
      const txRes = await axios.get(`${api.baseUrl}/payroll/transactions?year=${year}`);
      const txData = txRes.data;
      setPayrollTransactions(txData.transactions || []);
      
      // Load admin employees
      const empRes = await axios.get(`${api.baseUrl}/payroll/admin-employees`);
      const empData = empRes.data;
      setAdminEmployees(empData.employees || []);
      
      // Load classifications
      const classRes = await axios.get(`${api.baseUrl}/payroll/classifications?year=${year}`);
      const classData = classRes.data;
      setClassifications(classData.classifications || []);
      setSummary(classData.summary || null);
      
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // Save admin employee
  const saveAdminEmployee = async () => {
    try {
      const employee = editingEmployee || newEmployee;
      
      // Validate ventilation totals 100%
      const total = employee.ventilation.reduce((sum, v) => sum + v.pourcentage, 0);
      if (total !== 100) {
        toast.error("La ventilation doit totaliser 100%");
        return;
      }
      
      const response = await axios.post(`${api.baseUrl}/payroll/admin-employees`, employee);
      
      if (response.status === 200) {
        toast.success(editingEmployee ? "Employé mis à jour" : "Employé ajouté");
        setIsEmployeeDialogOpen(false);
        setEditingEmployee(null);
        setNewEmployee({ name: "", ventilation: [{ departement: "PRQC", pourcentage: 100 }] });
        loadData();
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Error saving employee:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  // Delete admin employee
  const deleteAdminEmployee = async (employeeId) => {
    try {
      const response = await axios.delete(`${api.baseUrl}/payroll/admin-employees/${employeeId}`);
      
      if (response.status === 200) {
        toast.success("Employé supprimé");
        loadData();
      }
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Open classify dialog
  const openClassifyDialog = (transaction) => {
    setClassifyingTransaction(transaction);
    
    // Initialize form with empty admin details for each employee
    const adminDetails = adminEmployees.map(emp => ({
      employee_id: emp.id,
      employee_name: emp.name,
      amount: 0
    }));
    
    setClassificationForm({
      adminDetails,
      terrainVentilation: [
        { departement: "PRQC", pourcentage: 60 },
        { departement: "LMDS", pourcentage: 30 },
        { departement: "Location", pourcentage: 10 }
      ]
    });
    
    setIsClassifyDialogOpen(true);
  };

  // Save classification
  const saveClassification = async () => {
    try {
      const totalAdmin = classificationForm.adminDetails.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
      const terrainAmount = classifyingTransaction.credit - totalAdmin;
      
      if (terrainAmount < 0) {
        toast.error("Le montant admin dépasse le montant total de la paie");
        return;
      }
      
      // Validate terrain ventilation
      const terrainTotal = classificationForm.terrainVentilation.reduce((sum, v) => sum + v.pourcentage, 0);
      if (terrainTotal !== 100) {
        toast.error("La ventilation terrain doit totaliser 100%");
        return;
      }
      
      const classification = {
        transaction_id: classifyingTransaction.transaction_id,
        date: classifyingTransaction.date,
        total_amount: classifyingTransaction.credit,
        admin_details: classificationForm.adminDetails.filter(d => d.amount > 0),
        terrain_amount: terrainAmount,
        terrain_ventilation: classificationForm.terrainVentilation
      };
      
      const response = await axios.post(`${api.baseUrl}/payroll/classify`, classification);
      
      if (response.status === 200) {
        toast.success("Paie classifiée avec succès");
        setIsClassifyDialogOpen(false);
        setClassifyingTransaction(null);
        loadData();
      } else {
        toast.error("Erreur lors de la classification");
      }
    } catch (error) {
      console.error("Error saving classification:", error);
      toast.error("Erreur lors de la classification");
    }
  };

  // Update employee ventilation
  const updateEmployeeVentilation = (index, field, value) => {
    const employee = editingEmployee || newEmployee;
    const newVentilation = [...employee.ventilation];
    newVentilation[index] = { ...newVentilation[index], [field]: value };
    
    if (editingEmployee) {
      setEditingEmployee({ ...editingEmployee, ventilation: newVentilation });
    } else {
      setNewEmployee({ ...newEmployee, ventilation: newVentilation });
    }
  };

  // Add ventilation row
  const addVentilationRow = () => {
    const employee = editingEmployee || newEmployee;
    const newVentilation = [...employee.ventilation, { departement: "LMDS", pourcentage: 0 }];
    
    if (editingEmployee) {
      setEditingEmployee({ ...editingEmployee, ventilation: newVentilation });
    } else {
      setNewEmployee({ ...newEmployee, ventilation: newVentilation });
    }
  };

  // Remove ventilation row
  const removeVentilationRow = (index) => {
    const employee = editingEmployee || newEmployee;
    const newVentilation = employee.ventilation.filter((_, i) => i !== index);
    
    if (editingEmployee) {
      setEditingEmployee({ ...editingEmployee, ventilation: newVentilation });
    } else {
      setNewEmployee({ ...newEmployee, ventilation: newVentilation });
    }
  };

  // Unclassified transactions
  const unclassifiedTransactions = payrollTransactions.filter(t => !t.is_classified);
  const classifiedTransactions = payrollTransactions.filter(t => t.is_classified);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ventilation-paie-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ventilation des Paies</h1>
          <p className="text-slate-500">Classifiez les paies en admin vs terrain par département</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">À classifier</p>
                <p className="text-2xl font-bold text-slate-900">{unclassifiedTransactions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Classifiées</p>
                <p className="text-2xl font-bold text-slate-900">{classifiedTransactions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Employés Admin</p>
                <p className="text-2xl font-bold text-slate-900">{adminEmployees.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Admin {year}</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(summary?.total_admin || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="classify" className="space-y-4">
        <TabsList>
          <TabsTrigger value="classify">
            <Calculator className="w-4 h-4 mr-2" />
            Classifier les paies
          </TabsTrigger>
          <TabsTrigger value="employees">
            <Users className="w-4 h-4 mr-2" />
            Employés Admin
          </TabsTrigger>
          <TabsTrigger value="summary">
            <Building2 className="w-4 h-4 mr-2" />
            Résumé par département
          </TabsTrigger>
        </TabsList>

        {/* Tab: Classifier les paies */}
        <TabsContent value="classify" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Paies à classifier ({unclassifiedTransactions.length})
              </CardTitle>
              <CardDescription>
                Cliquez sur une paie pour la classifier (séparer admin vs terrain)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {unclassifiedTransactions.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
                  <p>Toutes les paies de {year} sont classifiées!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {unclassifiedTransactions.slice(0, 20).map((tx) => (
                    <div 
                      key={tx.id || tx.transaction_id || `${tx.date}-${tx.description}-${tx.amount}`}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => openClassifyDialog(tx)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <DollarSign className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{tx.date}</p>
                          <p className="text-sm text-slate-500">{tx.name || tx.memo || 'Paie'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-lg text-slate-900">
                            {formatCurrency(tx.credit || tx.debit)}
                          </p>
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            À classifier
                          </Badge>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Paies déjà classifiées */}
          {classifiedTransactions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Paies classifiées ({classifiedTransactions.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {classifiedTransactions.slice(0, 10).map((tx) => (
                    <div 
                      key={tx.id || tx.transaction_id || `${tx.date}-${tx.description}-${tx.amount}`}
                      className="flex items-center justify-between p-3 border rounded-lg bg-emerald-50/50"
                    >
                      <div className="flex items-center gap-4">
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                        <div>
                          <p className="font-medium text-slate-900">{tx.date}</p>
                          <p className="text-sm text-slate-500">{tx.name || 'Paie'}</p>
                        </div>
                      </div>
                      <p className="font-bold text-slate-900">
                        {formatCurrency(tx.credit || tx.debit)}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab: Employés Admin */}
        <TabsContent value="employees" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Employés Administratifs</CardTitle>
                <CardDescription>
                  Définissez la ventilation par département pour chaque employé admin
                </CardDescription>
              </div>
              <Button 
                onClick={() => {
                  setEditingEmployee(null);
                  setNewEmployee({ name: "", ventilation: [{ departement: "PRQC", pourcentage: 100 }] });
                  setIsEmployeeDialogOpen(true);
                }}
                className="flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              {adminEmployees.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucun employé admin configuré</p>
                  <p className="text-sm">Ajoutez vos employés administratifs pour commencer</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {adminEmployees.map((emp) => (
                    <div 
                      key={emp.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-blue-100 rounded-full">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{emp.name}</p>
                          <div className="flex gap-2 mt-1">
                            {emp.ventilation.map((v) => (
                              <Badge key={`${v.departement}-${v.pourcentage}`} variant="secondary" className="text-xs">
                                {v.departement}: {v.pourcentage}%
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setEditingEmployee(emp);
                            setIsEmployeeDialogOpen(true);
                          }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteAdminEmployee(emp.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Résumé par département */}
        <TabsContent value="summary" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Admin par département */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Salaires Admin par département</CardTitle>
              </CardHeader>
              <CardContent>
                {summary?.admin_by_department ? (
                  <div className="space-y-3">
                    {Object.entries(summary.admin_by_department).map(([dept, amount]) => (
                      <div key={dept} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="font-medium">{dept}</span>
                        <span className="font-bold text-blue-600">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between p-3 bg-blue-100 rounded-lg mt-4">
                      <span className="font-bold">Total Admin</span>
                      <span className="font-bold text-blue-700">{formatCurrency(summary.total_admin)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Aucune donnée</p>
                )}
              </CardContent>
            </Card>

            {/* Terrain par département */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Salaires Terrain par département</CardTitle>
              </CardHeader>
              <CardContent>
                {summary?.terrain_by_department ? (
                  <div className="space-y-3">
                    {Object.entries(summary.terrain_by_department).map(([dept, amount]) => (
                      <div key={dept} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="font-medium">{dept}</span>
                        <span className="font-bold text-emerald-600">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between p-3 bg-emerald-100 rounded-lg mt-4">
                      <span className="font-bold">Total Terrain</span>
                      <span className="font-bold text-emerald-700">{formatCurrency(summary.total_terrain)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Aucune donnée</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog: Ajouter/Modifier employé admin */}
      <Dialog open={isEmployeeDialogOpen} onOpenChange={setIsEmployeeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingEmployee ? "Modifier l'employé admin" : "Ajouter un employé admin"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nom de l'employé</Label>
              <Input
                value={editingEmployee?.name || newEmployee.name}
                onChange={(e) => {
                  if (editingEmployee) {
                    setEditingEmployee({ ...editingEmployee, name: e.target.value });
                  } else {
                    setNewEmployee({ ...newEmployee, name: e.target.value });
                  }
                }}
                placeholder="Ex: Maissam"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Ventilation par département</Label>
              <div className="space-y-2">
                {(editingEmployee?.ventilation || newEmployee.ventilation).map((v, index) => (
                  <div key={v.id || v._id || index} className="flex items-center gap-2">
                    <Select 
                      value={v.departement}
                      onValueChange={(val) => updateEmployeeVentilation(index, 'departement', val)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DEPARTMENTS.filter(d => d !== "GLOBAL").map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={v.pourcentage}
                      onChange={(e) => updateEmployeeVentilation(index, 'pourcentage', parseInt(e.target.value) || 0)}
                      className="w-20"
                    />
                    <span className="text-slate-500">%</span>
                    {(editingEmployee?.ventilation || newEmployee.ventilation).length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeVentilationRow(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addVentilationRow}>
                  <Plus className="w-4 h-4 mr-1" /> Ajouter département
                </Button>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Total: {(editingEmployee?.ventilation || newEmployee.ventilation).reduce((sum, v) => sum + v.pourcentage, 0)}%
                {(editingEmployee?.ventilation || newEmployee.ventilation).reduce((sum, v) => sum + v.pourcentage, 0) !== 100 && (
                  <span className="text-red-500 ml-2">(doit être 100%)</span>
                )}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEmployeeDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={saveAdminEmployee}>
              <Save className="w-4 h-4 mr-2" />
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Classifier une paie */}
      <Dialog open={isClassifyDialogOpen} onOpenChange={setIsClassifyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Classifier la paie</DialogTitle>
          </DialogHeader>
          
          {classifyingTransaction && (
            <div className="space-y-4">
              {/* Info paie */}
              <div className="p-4 bg-slate-100 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-slate-500">Date</p>
                    <p className="font-medium">{classifyingTransaction.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Montant total</p>
                    <p className="text-xl font-bold text-slate-900">
                      {formatCurrency(classifyingTransaction.credit || classifyingTransaction.debit)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Montants Admin par employé */}
              <div>
                <Label className="mb-2 block font-medium">Montants Admin (par employé)</Label>
                {adminEmployees.length === 0 ? (
                  <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                    ⚠️ Ajoutez d'abord des employés admin dans l'onglet "Employés Admin"
                  </p>
                ) : (
                  <div className="space-y-2">
                    {classificationForm.adminDetails.map((detail, index) => (
                      <div key={detail.id || detail._id || index} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                        <span className="w-32 font-medium text-sm">{detail.employee_name}</span>
                        <Input
                          type="number"
                          min="0"
                          value={detail.amount || ''}
                          onChange={(e) => {
                            const newDetails = [...classificationForm.adminDetails];
                            newDetails[index].amount = parseFloat(e.target.value) || 0;
                            setClassificationForm({ ...classificationForm, adminDetails: newDetails });
                          }}
                          className="w-28"
                          placeholder="0"
                        />
                        <span className="text-slate-500">$</span>
                      </div>
                    ))}
                    <div className="flex justify-between p-2 bg-blue-50 rounded-lg mt-2">
                      <span className="font-medium">Total Admin:</span>
                      <span className="font-bold text-blue-600">
                        {formatCurrency(classificationForm.adminDetails.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0))}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Montant Terrain calculé */}
              <div className="p-3 bg-emerald-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Montant Terrain (calculé):</span>
                  <span className="font-bold text-emerald-600">
                    {formatCurrency(
                      (classifyingTransaction.credit || classifyingTransaction.debit) - 
                      classificationForm.adminDetails.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0)
                    )}
                  </span>
                </div>
              </div>

              {/* Ventilation Terrain */}
              <div>
                <Label className="mb-2 block font-medium">Ventilation Terrain par département</Label>
                <div className="space-y-2">
                  {classificationForm.terrainVentilation.map((v, index) => (
                    <div key={v.id || v._id || index} className="flex items-center gap-2">
                      <span className="w-24 text-sm font-medium">{v.departement}</span>
                      <Slider
                        value={[v.pourcentage]}
                        onValueChange={([val]) => {
                          const newVent = [...classificationForm.terrainVentilation];
                          newVent[index].pourcentage = val;
                          setClassificationForm({ ...classificationForm, terrainVentilation: newVent });
                        }}
                        max={100}
                        step={5}
                        className="flex-1"
                      />
                      <span className="w-12 text-right text-sm">{v.pourcentage}%</span>
                    </div>
                  ))}
                  <p className="text-sm text-slate-500">
                    Total: {classificationForm.terrainVentilation.reduce((sum, v) => sum + v.pourcentage, 0)}%
                    {classificationForm.terrainVentilation.reduce((sum, v) => sum + v.pourcentage, 0) !== 100 && (
                      <span className="text-red-500 ml-2">(doit être 100%)</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsClassifyDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={saveClassification} disabled={adminEmployees.length === 0}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Classifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VentilationPaie;
